// src/stores/settings/backup.store.js
import { defineStore } from "pinia";
import { ref } from "vue";
import BackupService from "@/services/settings/backup.service";
import { toast } from "@/utils/toast";

export const useBackupStore = defineStore("backups", () => {
  const systemBackups = ref([]);
  const databaseBackups = ref([]);
  const loading = ref(false);
  const generating = ref(false);

  // For delete modal
  const pendingDeleteIds = ref([]);
  const pendingDeleteType = ref(null); // 'system' | 'database' | null

  async function fetchAll() {
    loading.value = true;
    try {
      const res = await BackupService.getAll();
      systemBackups.value = res.data?.system || [];
      databaseBackups.value = res.data?.database || [];
    } catch (e) {
      toast(e?.response?.data?.message || "Failed to load backups", "error");
    } finally {
      loading.value = false;
    }
  }

  async function generate(type) {
    generating.value = true;
    try {
      await BackupService.generate(type);
      toast("Backup generated", "success");
      await fetchAll();
    } catch (e) {
      toast(e?.response?.data?.message || "Failed to generate backup", "error");
      throw e;
    } finally {
      generating.value = false;
    }
  }

  async function deleteOne(id) {
    loading.value = true;
    try {
      await BackupService.delete(id);
      toast("Backup deleted", "success");
      await fetchAll();
    } catch (e) {
      toast(e?.response?.data?.message || "Failed to delete backup", "error");
      throw e;
    } finally {
      loading.value = false;
    }
  }

  async function bulkDelete(ids) {
    if (!ids?.length) return;
    loading.value = true;
    try {
      await BackupService.bulkDelete(ids);
      toast("Backups deleted", "success");
      await fetchAll();
    } catch (e) {
      toast(e?.response?.data?.message || "Failed to delete backups", "error");
      throw e;
    } finally {
      loading.value = false;
    }
  }

  // ---------- Delete modal helpers ----------
  function setPendingDelete(ids, type = null) {
    pendingDeleteIds.value = Array.isArray(ids) ? ids : [ids];
    pendingDeleteType.value = type;
  }

  async function confirmPendingDelete() {
    if (!pendingDeleteIds.value.length) return;
    // for now we don't need type at API level, but we keep it for relevance
    await bulkDelete(pendingDeleteIds.value);
    pendingDeleteIds.value = [];
    pendingDeleteType.value = null;
  }

  return {
    systemBackups,
    databaseBackups,
    loading,
    generating,
    fetchAll,
    generate,
    deleteOne,
    bulkDelete,
    // delete modal helpers
    pendingDeleteIds,
    pendingDeleteType,
    setPendingDelete,
    confirmPendingDelete,
  };
});
